import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './authConfig';

interface Module {
  ModuleId: number;
  name: string;
  DisplayName: string;
}

interface License {
  LicenseID: number;
  CustomerID: number;
  StartDate: string;
  ExpiryDate: string;
  Type: string;
  IsActive: boolean;
  Name: string;
  DisplayName: string;
  Description: string;
  modules: Module[];
}

interface FeatureFlag {
  FeatureFlagID: number;
  FeatureName: string;
  Description: string | null;
  Status: string;
  CreatedDate: string;
  IsGloballyEnabled: boolean;
  IsEnabled: boolean;
  EnabledDate: string | null;
}

export interface SignInState {
  customerInfo: { CustomerID: number, TenantID: string, CompanyName: string, TenantInt: number, IsActive: boolean, CreationDate: string }[];
  licenseInfo: License[];
  userInfo: { PrimaryCustomerID: number, IsActive: boolean, UserName: string, Email: string, FirstName: string, LastName: string, DisplayName: string, PhoneNumber: string, UserID: number, ThemePreference: string, CreationDate: string }[];
  userFeatures: { CustomerID: number, FeatureID: number, FeatureName: string, Enabled: boolean }[];
  userRoles: { CustomerID: number, RoleID: number, RoleName: string }[];
  featureFlags: FeatureFlag[] | null;
  isAuthed: boolean;
  isVerified: boolean;
  hasAccount: boolean;
  currentCustomerID: number;
  isLoading: boolean;
}

interface SignInStateContextType {
  signInState: SignInState;
  setSignInState: React.Dispatch<React.SetStateAction<SignInState>>;
  // navDrawerState: NavDrawerState;
  // setNavDrawerState: React.Dispatch<React.SetStateAction<NavDrawerState>>;
}

interface NavDrawerState {
  isOpen: boolean;
}

// Create a context
const SignInStateContext = createContext<SignInStateContextType | null>(null);

// Create a provider component
export const SignInStateProvider = ({ children }: { children: React.ReactNode }) => {
  const [signInState, setSignInState] = useState<SignInState>({
    customerInfo: [],
    licenseInfo: [],
    userInfo: [],
    userFeatures: [],
    userRoles: [],
    featureFlags: null,
    isAuthed: false,
    isVerified: false,
    hasAccount: false,
    currentCustomerID: 0,
    isLoading: true,
  });

  const [ navDrawerState, setNavDrawerState ] = useState<NavDrawerState>({ isOpen: false });

  const { instance, accounts } = useMsal();
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);

  useEffect(() => {
    const initializeMsal = async () => {
      const msalInstance = new PublicClientApplication(msalConfig);

      await msalInstance.initialize();

      setIsMsalInitialized(true);
    };

    initializeMsal();
  }, []);


  const apiUrl = process.env.REACT_APP_API_URL;

  const processToken = async ({accessToken}: {accessToken: string}) => {
    const response = await fetch(`${apiUrl}initial/signin`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        "Authorization": `Bearer ${accessToken}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      setSignInState({ ...data, featureFlags: null, isLoading: false});
    }
    else {
      setSignInState({ customerInfo: [], licenseInfo: [], userInfo: [], userFeatures: [], userRoles: [], featureFlags: null, isAuthed: false, isVerified: false,hasAccount: false, currentCustomerID: 0, isLoading: false});
    }
  }

  useEffect(() => {
    const checkSignInState = async () => {
      let currentCustomerTid = signInState.customerInfo.find((customer: { CustomerID: number; }) => customer.CustomerID === signInState.currentCustomerID)?.TenantID;
      console.log('Current Customer TID:', currentCustomerTid);
      let currentAccount = currentCustomerTid ? accounts.find(account => account.tenantId === currentCustomerTid) : accounts[0];
      if (!currentAccount && currentCustomerTid && accounts.length) {
        currentAccount = accounts[0];
      }

      if (!currentCustomerTid) {
        currentCustomerTid = currentAccount?.tenantId;
      }
      
      try {
        const silentResult = await instance.acquireTokenSilent({
          scopes: [`api://${process.env.REACT_APP_CLIENT_APP_ID}/AppUser`],
          authority: `https://login.microsoftonline.com/${currentCustomerTid}`,
          account: currentAccount,
        });

        await processToken(silentResult);
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            // User interaction is required, initiate interactive login
            const accessTokenResponse = await instance.acquireTokenPopup({
              scopes: [`api://${process.env.REACT_APP_CLIENT_APP_ID}/AppUser`],
              authority: `https://login.microsoftonline.com/${currentCustomerTid}`,
              account: currentAccount,
            });

            await processToken(accessTokenResponse);
        } else {
            console.error("Other MSAL error:", error);
            setSignInState({ customerInfo: [], licenseInfo: [], userInfo: [], userFeatures: [], userRoles: [], featureFlags: null, isAuthed: false, isVerified: false,hasAccount: false, currentCustomerID: 0, isLoading: false});
        }
      }
    }
    if (isMsalInitialized && accounts.length > 0) {
      checkSignInState()
    }
  }, [instance, accounts, isMsalInitialized]);

  const contextValue = useMemo(() => ({ signInState, setSignInState }), [signInState, setSignInState]);

  return (
    // <SignInStateContext.Provider value={{ signInState, setSignInState, navDrawerState, setNavDrawerState }}></SignInStateContext.Provider>
    <SignInStateContext.Provider value={contextValue}>
      {children}
    </SignInStateContext.Provider>
  );
};

// Create a custom hook for using the signInState context
export const useSignInState = (): SignInStateContextType => {
  const context = useContext(SignInStateContext);
  if (!context) {
    console.log('useSignInState must be used within a SignInStateProvider');
    throw new Error('useSignInState must be used within a SignInStateProvider');
  }
  return context;
}